import { memo } from "react";
import { Skeleton } from "components/ui/skeleton";
import { formatCurrency } from "shared/utils/currency";
import type { StatsBlock, StatsTypeBlock, StatsBucket } from "shared/types/renovation";

interface StatusBucketsProps {
  typeBlock: StatsTypeBlock;
}

const STATUS_BUCKETS: { key: keyof StatsTypeBlock; label: string; className: string }[] = [
  {
    key: "goedgekeurd",
    label: "Goedgekeurd",
    className: "border-green-200 bg-green-50 text-green-700",
  },
  {
    key: "ontvangen",
    label: "Ontvangen",
    className: "border-blue-200 bg-blue-50 text-blue-700",
  },
  {
    key: "afgewezen",
    label: "Afgewezen",
    className: "border-red-200 bg-red-50 text-red-700",
  },
  {
    key: "concept",
    label: "Concept",
    className: "border-neutral-200 bg-neutral-50 text-neutral-secondary-mid",
  },
];

const StatusBuckets = memo(({ typeBlock }: StatusBucketsProps) => (
  <div className="flex flex-wrap gap-1">
    {STATUS_BUCKETS.map(({ key, label, className }) => {
      const bucket = typeBlock[key] as StatsBucket;
      if (bucket.count === 0) return null;
      return (
        <span
          key={key}
          className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${className}`}
          title={`${label}: ${bucket.count} document(en) · ${formatCurrency(bucket.amount)}`}
        >
          {label} ({bucket.count}) · {formatCurrency(bucket.amount)}
        </span>
      );
    })}
  </div>
));
StatusBuckets.displayName = "StatusBuckets";

interface TypeRowProps {
  label: string;
  typeBlock: StatsTypeBlock;
  /** Enkel het aantal tonen, zonder het opgeteld bedrag. */
  countOnly?: boolean;
  /**
   * Toon de regel ook wanneer er (nog) geen documenten van dit type zijn.
   *
   * Voor de drie financiële types — offerte, bestelbon, factuur — is "nul" een
   * antwoord: dan weet je dat er nog niets besteld of gefactureerd is. Werd de
   * regel weggelaten, dan leek het cijfer te ontbreken in plaats van nul te zijn
   * (precies wat er bij de bestelbonnen gebeurde toen die net opgeladen konden
   * worden). Contracten en Overig blijven verborgen bij nul: die zijn
   * uitzondering, geen keten.
   */
  alwaysShow?: boolean;
}

const TypeRow = memo(({ label, typeBlock, countOnly, alwaysShow }: TypeRowProps) => {
  if (typeBlock.total.count === 0 && !alwaysShow) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {/* Btw-basis pal naast het type i.p.v. achter de bedragen: de budgetregel eronder
          staat incl. btw, dus het hoort bij het label te staan waar je het leest. */}
      <span className="text-neutral-secondary-mid w-36 shrink-0 text-[11px] font-semibold tracking-wide uppercase">
        {label}{" "}
        <span className="font-medium normal-case">excl. btw</span>
      </span>
      <span className="text-neutral-primary text-xs font-semibold tabular-nums whitespace-nowrap">
        {typeBlock.total.count}
        {!countOnly && ` · ${formatCurrency(typeBlock.total.amount)}`}
      </span>
      <StatusBuckets typeBlock={typeBlock} />
    </div>
  );
});
TypeRow.displayName = "TypeRow";

const TYPE_ENTRIES: Array<{
  key: keyof StatsBlock["byType"];
  label: string;
  countOnly?: boolean;
  alwaysShow?: boolean;
}> = [
  // De keten offerte → bestelbon → factuur staat er altijd, ook op nul.
  //
  // Offertes: enkel het aantal. Het opgeteld offertebedrag zei niets — voor dezelfde
  // werken liggen er vaak meerdere offertes van verschillende leveranciers, dus die som
  // telt hetzelfde werk meermaals mee.
  { key: "offerte", label: "Offertes", countOnly: true, alwaysShow: true },
  { key: "bestelbon", label: "Bestelbonnen", alwaysShow: true },
  { key: "factuur", label: "Facturen", alwaysShow: true },
  { key: "contract", label: "Contracten" },
  { key: "overig", label: "Overig" },
];

/**
 * Btw-tarief waarmee het budget en het gefactureerde bedrag naar **incl. btw** gerekend
 * worden.
 *
 * Bewust één constante en zichtbaar in de UI: de investeringslijnen bewaren enkel
 * `estimatedCostExclVat` — er staat geen btw-tarief bij een budget. Een bedrag incl. btw is
 * dus altijd een omrekening, en dan moet er bij staan met welk percentage. Voor
 * renovatie aan woningen ouder dan 10 jaar geldt in België vaak 6 % i.p.v. 21 %; zet dit
 * dus juist voor jullie situatie.
 */
const BUDGET_VAT_RATE = 0.21;
const inclVat = (exclVat: number) => exclVat * (1 + BUDGET_VAT_RATE);

interface BudgetRowProps {
  /** Voorzien budget excl. btw (investeringslijnen), of null wanneer onbekend. */
  netBudgetExclVat: number | null;
  /** Gefactureerd excl. btw — de Facturen-rij van dit blok. */
  invoicedExclVat: number;
}

/**
 * Budgetregel onder de types: **gefactureerd / budget** met het gebruikte percentage.
 *
 * Beide bedragen incl. btw, omgerekend met {@link BUDGET_VAT_RATE}; de types hierboven
 * staan excl. btw. Dat verschil staat er expliciet bij, want twee btw-bases in één blok
 * zijn anders niet te lezen.
 */
const BudgetRow = memo(({ netBudgetExclVat, invoicedExclVat }: BudgetRowProps) => {
  if (netBudgetExclVat === null) return null;

  const budget = inclVat(netBudgetExclVat);
  const invoiced = inclVat(invoicedExclVat);
  const percentage = budget > 0 ? Math.round((invoiced / budget) * 100) : 0;
  const overBudget = budget > 0 && invoiced > budget;

  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-neutral-100 pt-1.5">
      <span className="text-neutral-secondary-mid w-36 shrink-0 text-[11px] font-semibold tracking-wide uppercase">
        Budget <span className="font-medium normal-case">incl. btw</span>
      </span>
      <span
        className={`text-xs font-semibold tabular-nums whitespace-nowrap ${
          overBudget ? "text-red-600" : "text-neutral-primary"
        }`}
        title={
          budget > 0
            ? `Gefactureerd ${formatCurrency(invoiced)} van ${formatCurrency(budget)} voorzien budget — incl. ${Math.round(BUDGET_VAT_RATE * 100)} % btw`
            : "Geen budget voorzien op de investeringslijnen van deze selectie"
        }
      >
        {formatCurrency(invoiced)} / {formatCurrency(budget)}
        {budget > 0 && ` (${percentage} %)`}
      </span>
      <span className="text-neutral-secondary-mid text-[10px]">
        uit de investeringslijnen
      </span>
    </div>
  );
});
BudgetRow.displayName = "BudgetRow";

interface StatsBlockViewProps {
  title: string;
  block: StatsBlock | null;
  isLoading: boolean;
  variant: "total" | "filtered";
  /** Voorzien budget excl. btw; alleen meegegeven aan het blok dat de scope volgt. */
  netBudgetExclVat?: number | null;
}

const StatsBlockView = memo(({
  title,
  block,
  isLoading,
  variant,
  netBudgetExclVat = null,
}: StatsBlockViewProps) => {
  const accent =
    variant === "total"
      ? "border-l-moss-primary"
      : "border-l-brick-primary";

  return (
    <div
      className={`flex-1 min-w-0 rounded-md border border-neutral-200 border-l-4 ${accent} bg-white px-3 py-2`}
    >
      {/* De btw-basis staat per regel (excl. btw bij de types, incl. btw bij het
          budget) en niet meer één keer hier: één noot boven het blok zou fout zijn
          zodra er twee bases in staan. */}
      <div className="text-neutral-secondary-mid text-[10px] font-semibold tracking-wider uppercase">
        {title}
      </div>
      {isLoading || !block ? (
        <div className="mt-1.5 flex flex-col gap-1.5">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
      ) : (
        <>
          {/* Geen kopregel met "N documenten · € totaal" meer: dat totaal telde
              offertes, facturen, bestelbonnen en contracten bij elkaar op en betekende
              dus niets. De opsplitsing per type hieronder is wat je wil zien. */}
          <div className="mt-1.5 flex flex-col gap-1">
            {TYPE_ENTRIES.map(({ key, label, countOnly, alwaysShow }) => (
              <TypeRow
                key={key}
                label={label}
                typeBlock={block.byType[key]}
                countOnly={countOnly}
                alwaysShow={alwaysShow}
              />
            ))}
            <BudgetRow
              netBudgetExclVat={netBudgetExclVat}
              invoicedExclVat={block.byType.factuur.total.amount}
            />
          </div>
        </>
      )}
    </div>
  );
});
StatsBlockView.displayName = "StatsBlockView";

export interface RenovationDocumentsStatsProps {
  total: StatsBlock | null;
  filtered: StatsBlock | null;
  hasActiveFilters: boolean;
  isLoading: boolean;
  /**
   * When false, only the "Gefilterd resultaat" block is shown. The unfiltered
   * "Alle documenten" block is hidden because it is just the filtered block
   * without any filters applied (remove the filter to see all). Defaults to
   * true for backwards compatibility (embedded mode keeps the side-by-side view).
   */
  showTotal?: boolean;
  /**
   * Gebouw waarop gefilterd staat. Komt tussen haakjes achter "Gefilterd resultaat", zodat
   * er staat *waarop* gefilterd is en niet enkel *dat* er gefilterd is.
   */
  buildingId?: string;
  /**
   * Voorzien budget (excl. btw) van de investeringslijnen binnen dezelfde scope. Komt als
   * budgetregel onder de types te staan, omgerekend naar incl. btw. `null`/weggelaten =
   * geen budgetregel.
   */
  netBudgetExclVat?: number | null;
}

/**
 * RenovationDocumentsStats
 *
 * Two side-by-side stat blocks always rendered so the user can compare:
 *  - "Alle documenten"    — server total (scoped to buildingId if set)
 *  - "Gefilterd resultaat" — filters applied
 * When no filters are active the two blocks show identical numbers — that is
 * the intended UX (always-on comparison).
 */
export const RenovationDocumentsStats = memo(
  ({
    total,
    filtered,
    hasActiveFilters,
    isLoading,
    showTotal = true,
    buildingId,
    netBudgetExclVat = null,
  }: RenovationDocumentsStatsProps) => {
    // Wát er gefilterd is tussen haakjes: het gebouw wanneer dat bekend is, anders
    // gewoon dat er filters aan staan.
    const filteredTitle = buildingId
      ? `Gefilterd resultaat (gebouw ${buildingId})`
      : hasActiveFilters
        ? "Gefilterd resultaat (filters actief)"
        : "Gefilterd resultaat (geen filters actief)";

    return (
      <div className="px-6 pt-3 pb-2">
        <div className="flex flex-col gap-2 lg:flex-row">
          {showTotal && (
            <StatsBlockView
              title="Alle documenten"
              block={total}
              isLoading={isLoading}
              variant="total"
            />
          )}
          {/* De budgetregel hangt aan het gefilterde blok: het budget wordt op dezelfde
              scope opgehaald als de documenten. */}
          <StatsBlockView
            title={filteredTitle}
            block={filtered}
            isLoading={isLoading}
            variant="filtered"
            netBudgetExclVat={netBudgetExclVat}
          />
        </div>
      </div>
    );
  }
);
RenovationDocumentsStats.displayName = "RenovationDocumentsStats";
